import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import SaveIcon from '@mui/icons-material/Save';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import OutlinedInput from '@mui/material/OutlinedInput';
import Skeleton from '@mui/material/Skeleton';
import { Stack } from '@mui/system';
import CardView from '../../components/Redesing/card-view';
import FormItem from '../../components/Redesing/form-item';
import IconButtonMenu from '../../components/Redesing/icon-button-menu';
import { findAccess } from '../../utils';
import {
  SystemConfig,
  useSystemConfigRelay,
} from '../../hooks/system/query/V2/useSystemConfigRelay';
import { usePatchSystemConfigMutation } from '../../hooks/system/mutation/V2/useSystemConfigMutations';

/** System.config key holding the number of contracted rentable units. */
export const CONTRACTED_ASSETS_KEY = 'contracted_assets';

export const CONTRACTED_ASSETS_DESCRIPTION =
  'Number of rentable units that is expected to be provided for rentals.';

export const INVALID_CONTRACTED_ASSETS_MESSAGE = 'Enter a whole number of 0 or more.';

/** Height of a default MUI OutlinedInput, so the skeleton holds its footprint. */
const INPUT_HEIGHT = 56;

/**
 * The Fleet tab is Movatic-only for now: the same gate the rental details page
 * uses for its Analytics tab (`findAccess` reports `movaticAccess` for -1).
 */
export function canSeeFleetTab(systemAccess: number | null | undefined): boolean {
  if (systemAccess === null || systemAccess === undefined) return false;
  return findAccess(systemAccess).includes('movaticAccess');
}

/**
 * Parses the raw input into the value stored in config. Empty means null (the
 * field is nullable); anything other than a non-negative integer is invalid.
 */
export function parseContractedAssets(draft: string): {
  value: number | null;
  isValid: boolean;
} {
  const trimmed = draft.trim();
  if (trimmed === '') return { value: null, isValid: true };
  if (!/^\d+$/.test(trimmed)) return { value: null, isValid: false };
  return { value: Number(trimmed), isValid: true };
}

function toDraft(config: SystemConfig | null | undefined): string {
  const stored = config?.[CONTRACTED_ASSETS_KEY];
  return stored === null || stored === undefined ? '' : String(stored);
}

interface IFleetSettingsProps {
  /** The system's global id (gid). */
  systemId: string;
  /** Page-level dirty flag; drives the Settings page's unsaved-changes modal. */
  isChanged: (flag: boolean) => void;
  /** Page-level Save gate: true until the page has been told of a change. */
  buttonDisabled: boolean;
}

const FleetSettings: React.FC<IFleetSettingsProps> = ({
  systemId,
  isChanged,
  buttonDisabled,
}) => {
  const { config, isLoading } = useSystemConfigRelay(systemId);
  const { patchSystemConfig, isLoading: isSaving } = usePatchSystemConfigMutation();
  const [draft, setDraft] = useState<string>(() => toDraft(config));

  useEffect(() => {
    if (!isLoading) setDraft(toDraft(config));
  }, [config, isLoading]);

  const parsed = useMemo(() => parseContractedAssets(draft), [draft]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDraft(event.target.value);
    isChanged(true);
  };

  const handleSave = async () => {
    if (!parsed.isValid) return;

    const request = patchSystemConfig(systemId, {
      [CONTRACTED_ASSETS_KEY]: parsed.value,
    }).then((result) => {
      if (!result.isSuccess) {
        throw new Error(result.errors[0]?.message || 'Failed to update Fleet settings');
      }
      return result;
    });

    toast.promise(request, {
      loading: 'Updating Fleet settings',
      success: 'Fleet settings updated',
      error: (err: Error) => err.message || 'Failed to update Fleet settings',
    });

    try {
      const result = await request;
      setDraft(toDraft(result.data?.config as SystemConfig | null | undefined));
      isChanged(false);
    } catch {
      // Reported through the toast; the unsaved draft stays put.
    }
  };

  return (
    <CardView
      title="Fleet"
      content={
        <Stack px={1.5} pb={2}>
          <FormItem
            label="Contracted assets"
            description={CONTRACTED_ASSETS_DESCRIPTION}
            content={
              isLoading ? (
                // Same treatment as the Terms tab: hold the input's footprint so
                // the field never flashes empty before the stored value arrives.
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={INPUT_HEIGHT}
                  aria-busy="true"
                  aria-label="Loading contracted assets"
                />
              ) : (
                <FormControl fullWidth error={!parsed.isValid}>
                  <OutlinedInput
                    id="contractedAssets"
                    name="contractedAssets"
                    type="number"
                    fullWidth
                    value={draft}
                    onChange={handleChange}
                    inputProps={{ min: 0, step: 1, 'aria-label': 'Contracted assets' }}
                  />
                  {!parsed.isValid && (
                    <FormHelperText>{INVALID_CONTRACTED_ASSETS_MESSAGE}</FormHelperText>
                  )}
                </FormControl>
              )
            }
          />
        </Stack>
      }
      headerActions={
        <IconButtonMenu
          buttons={[
            {
              label: 'Save',
              startIcon: <SaveIcon />,
              onClick: handleSave,
              disabled: buttonDisabled || isLoading || isSaving || !parsed.isValid,
            },
          ]}
        />
      }
    />
  );
};

export default FleetSettings;
